import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  CheckCircle2, Clock, AlertTriangle,
  Eye, Search, Plus, Building2, Mail,
  RefreshCw, Loader2, ThumbsUp, ThumbsDown, CircleDashed,
} from 'lucide-react';
import { onboardingAPI, suppliersAPI, intelligenceAPI, SupplierInvitation, OnboardingWorkflow, OnboardingDashboardStats, FilledDataset, PendingInitialSupplier, PendingSupplierDetail, SupplierProfile } from '../../lib/api';
import { toast } from 'sonner';
import { useIsSupplier } from '../../hooks/usePortalMode';
import { formatDistanceToNow, format } from 'date-fns';
import { statusColors } from './onboardingConstants';
import { SupplierOnboardingView } from './SupplierOnboardingView';
import { InvitationsTab, PendingReviewsTab } from './CustomerOnboardingTabs';

interface FilledDatasetWithDetails extends FilledDataset {
  supplier_name?: string;
  dataset_name?: string;
}

// CR-4817: Pending Approvals assign list merges two sources that track
// questionnaires separately in the backend — OnboardingDataset (used by the
// onboarding pipeline) and QualificationTemplate (authored on the Questionnaires
// page). The dropdown treats them uniformly; the approve handler splits by
// source and sends each list to the right backend field.
type AssignableTemplateSource = 'dataset' | 'qualification';
interface AssignableTemplate {
  key: string;            // compound key (source + id) for React + selection state
  id: number;             // numeric id sent back to the backend
  source: AssignableTemplateSource;
  name: string;
  type: string;           // dataset_type or template_type — for display only
  version: string;
  questions_count: number;
}

export default function OnboardingDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSupplier = useIsSupplier();
  const [activeTab, setActiveTab] = useState('drafts');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<OnboardingDashboardStats | null>(null);
  const [invitations, setInvitations] = useState<SupplierInvitation[]>([]);
  const [workflows, setWorkflows] = useState<OnboardingWorkflow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [myFilledDatasets, setMyFilledDatasets] = useState<FilledDataset[]>([]);
  const [myWorkflow, setMyWorkflow] = useState<OnboardingWorkflow | null>(null);
  const [pendingReviews, setPendingReviews] = useState<FilledDatasetWithDetails[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<SupplierInvitation[]>([]);
  const [pendingInitialSuppliers, setPendingInitialSuppliers] = useState<PendingInitialSupplier[]>([]);
  // Drafts = pre-wizard-submission supplier profiles (SupplierProfile.onboarding_status='draft').
  const [draftSuppliers, setDraftSuppliers] = useState<SupplierProfile[]>([]);
  // Approved suppliers (SupplierProfile.is_approved=true) for the Approved tab.
  const [approvedSuppliers, setApprovedSuppliers] = useState<SupplierProfile[]>([]);
  // Page indexes for client-side pagination (per tab, 1-based).
  const [draftPage, setDraftPage] = useState(1);
  const [approvedPage, setApprovedPage] = useState(1);
  const TAB_PAGE_SIZE = 25;
  const [availableTemplates, setAvailableTemplates] = useState<AssignableTemplate[]>([]);
  // Selection is stored by compound key so the two ID namespaces don't collide.
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSupplierId, setReviewSupplierId] = useState<number | null>(null);
  const [supplierDetail, setSupplierDetail] = useState<PendingSupplierDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | string | null>(null);
  // datasets state removed - no longer needed after tab restructure
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectTarget, setRejectTarget] = useState<{ type: 'invitation' | 'supplier'; id: number } | null>(null);

  useEffect(() => {
    if (isSupplier) {
      fetchSupplierData();
    } else {
      fetchData();
    }
  }, [isSupplier]);

  // Auto-open review modal when navigated with ?review=<profileId>
  useEffect(() => {
    const reviewId = searchParams.get('review');
    if (reviewId && !loading) {
      const profileId = Number(reviewId);
      if (profileId > 0) {
        handleOpenReviewModal(profileId);
        // Clear the query param so refreshing doesn't re-open
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, loading]);

  // CR-4758: Honour ?tab=<value> so the wizard can deep-link to a specific
  // tab (e.g. Pending Approvals after a new supplier is submitted).
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
      // Drop the query param so a refresh doesn't bounce the user away from
      // wherever they navigate next.
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchSupplierData = async () => {
    setLoading(true);
    try {
      const [filledRes, workflowsRes] = await Promise.all([
        onboardingAPI.getFilledDatasets(),
        onboardingAPI.getWorkflows(),
      ]);
      const filledData = filledRes.data.results || filledRes.data || [];
      setMyFilledDatasets(filledData);
      const wfData = workflowsRes.data.results || workflowsRes.data || [];
      if (wfData.length > 0) setMyWorkflow(wfData[0]);
    } catch (error) {
      console.error('Failed to fetch supplier onboarding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // CR-4799: Pending Due Diligence used to only surface status='submitted',
      // so once a customer rejected a questionnaire or asked for more
      // information the record disappeared from the tab with no other place
      // to track it. Fetch every status the customer can still act on — the
      // tab now includes rejected / more_info_required / under_review rows
      // alongside freshly submitted ones, and each row's badge surfaces the
      // specific state.
      const PENDING_DUE_DILIGENCE_STATUSES = ['submitted', 'under_review', 'more_info_required', 'rejected'];
      const [dashboardRes, invitationsRes, workflowsRes, filledDatasetsResArr, pendingApprovalsRes, pendingInitialRes, datasetsRes, qualificationTemplatesRes, draftSuppliersRes, approvedSuppliersRes] = await Promise.all([
        onboardingAPI.getDashboard(),
        onboardingAPI.getInvitations(),
        onboardingAPI.getWorkflows(),
        Promise.all(PENDING_DUE_DILIGENCE_STATUSES.map(s =>
          onboardingAPI.getFilledDatasets({ status: s, page_size: 1000 })
        )),
        onboardingAPI.getPendingApprovals(),
        onboardingAPI.getPendingInitialSuppliers(),
        // CR-4817: assign-list now unions both questionnaire sources — the
        // onboarding pipeline's OnboardingDataset AND the Questionnaires page's
        // QualificationTemplate — because customers create on the latter but
        // the previous behaviour only listed the former, hiding freshly
        // authored questionnaires (e.g. "Test Assessment") from the list.
        onboardingAPI.getDatasets({ is_active: true, page_size: 200 }),
        intelligenceAPI.getTemplates({ status: 'active', page_size: 200 }),
        // Drafts = wizard-in-progress profiles; supplier hasn't submitted for review yet.
        suppliersAPI.getProfiles({ minimal: true, onboarding_status: 'draft', page_size: 1000 }),
        // Approved = fully onboarded suppliers.
        suppliersAPI.getProfiles({ minimal: true, is_approved: true, page_size: 1000 }),
      ]);
      setStats(dashboardRes.data);
      setInvitations(invitationsRes.data.results || invitationsRes.data || []);
      const allWorkflows = workflowsRes.data.results || workflowsRes.data || [];
      setWorkflows(allWorkflows);
      setDraftSuppliers(draftSuppliersRes.data.results || []);
      setApprovedSuppliers(approvedSuppliersRes.data.results || []);
      // CR-4799: flatten multi-status responses into one list; dedupe by id
      // in case the backend ever widens a state machine and a row transitions
      // between fetches (defensive — unlikely in practice given Promise.all).
      const combinedFilled = filledDatasetsResArr.flatMap(r => r.data.results || r.data || []);
      const seenIds = new Set<number>();
      const uniqueFilled = combinedFilled.filter(d => {
        if (seenIds.has(d.id)) return false;
        seenIds.add(d.id);
        return true;
      });
      setPendingReviews(uniqueFilled);
      setPendingApprovals(pendingApprovalsRes.data.results || pendingApprovalsRes.data || []);
      setPendingInitialSuppliers(pendingInitialRes.data.results || []);

      // CR-4817 + CR-4827: merge OnboardingDataset and QualificationTemplate
      // sources into one dropdown, then dedupe to one row per name.
      // Version strings like "Issue 1/2/3" and "Latest" are authored per-template;
      // prefer "Latest" when present, otherwise fall back to the lexicographically
      // greatest version string (stable for the Issue N pattern). Without this
      // the dropdown showed Data Protection / ESG / Governance four times each
      // (one per issue), which is what CR-4827 is about.
      const datasets = datasetsRes.data.results || [];
      const qtemplates = qualificationTemplatesRes.data.results || [];
      const merged: AssignableTemplate[] = [
        ...datasets.map((d): AssignableTemplate => ({
          key: `d:${d.id}`,
          id: d.id,
          source: 'dataset',
          name: d.name,
          type: d.dataset_type || '',
          version: d.version || '',
          questions_count: d.questions_count || 0,
        })),
        ...qtemplates.map((t): AssignableTemplate => ({
          key: `q:${t.id}`,
          id: t.id,
          source: 'qualification',
          name: t.name,
          type: t.template_type || '',
          version: String(t.version || ''),
          questions_count: t.question_count || 0,
        })),
      ];
      const versionRank = (v: string): number => {
        const s = (v || '').trim().toLowerCase();
        if (s === 'latest') return Number.MAX_SAFE_INTEGER;
        const m = s.match(/(\d+(?:\.\d+)?)/);
        return m ? parseFloat(m[1]) : -1;
      };
      const byName = new Map<string, AssignableTemplate>();
      for (const t of merged) {
        const k = t.name.trim().toLowerCase();
        const prev = byName.get(k);
        if (!prev || versionRank(t.version) > versionRank(prev.version)) {
          byName.set(k, t);
        }
      }
      setAvailableTemplates(Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to fetch onboarding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async (id: number) => {
    try { await onboardingAPI.sendInvitation(id); fetchData(); }
    catch (error) { console.error('Failed to send invitation:', error); }
  };

  const handleResendInvitation = async (id: number) => {
    try { await onboardingAPI.resendInvitation(id); fetchData(); }
    catch (error) { console.error('Failed to resend invitation:', error); }
  };

  const handleApproveToSend = async (id: number) => {
    setActionLoading(id);
    try { await onboardingAPI.approveToSend(id); fetchData(); }
    catch (error) { console.error('Failed to approve invitation:', error); }
    finally { setActionLoading(null); }
  };

  const handleRejectApproval = (id: number) => {
    setRejectTarget({ type: 'invitation', id });
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleOpenReviewModal = async (profileId: number) => {
    setReviewSupplierId(profileId);
    setReviewModalOpen(true);
    setSupplierDetail(null);
    setDetailLoading(true);
    try {
      const res = await onboardingAPI.getPendingSupplierDetail(profileId);
      setSupplierDetail(res.data);
    } catch (error) {
      console.error('Failed to load supplier details:', error);
      toast.error('Failed to load supplier details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseReviewModal = () => {
    setReviewModalOpen(false);
    setReviewSupplierId(null);
    setSupplierDetail(null);
  };

  const handleApprovePendingSupplier = async (profileId: number) => {
    setActionLoading(`supplier-${profileId}`);
    try {
      // CR-4817: split selected compound keys by source and send each list as
      // the matching backend field. Datasets → template_ids, qualification
      // templates → qualification_template_ids.
      const templateIds: number[] = [];
      const qualificationTemplateIds: number[] = [];
      for (const key of selectedTemplateIds) {
        const picked = availableTemplates.find(t => t.key === key);
        if (!picked) continue;
        (picked.source === 'qualification' ? qualificationTemplateIds : templateIds).push(picked.id);
      }
      const res = await onboardingAPI.approvePendingSupplier(profileId, {
        templateIds: templateIds.length > 0 ? templateIds : undefined,
        qualificationTemplateIds: qualificationTemplateIds.length > 0 ? qualificationTemplateIds : undefined,
      });
      toast.success(`Supplier approved. ${res.data.questionnaires_assigned} questionnaire(s) assigned.`);
      handleCloseReviewModal();
      fetchData();
    } catch (error) {
      console.error('Failed to approve supplier:', error);
      toast.error('Failed to approve supplier');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectPendingSupplier = (profileId: number) => {
    setRejectTarget({ type: 'supplier', id: profileId });
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    const { type, id } = rejectTarget;
    if (type === 'invitation') {
      setActionLoading(id);
      try { await onboardingAPI.rejectApproval(id, rejectReason); fetchData(); }
      catch { toast.error('Failed to reject invitation'); }
      finally { setActionLoading(null); }
    } else {
      setActionLoading(`supplier-${id}`);
      try {
        await onboardingAPI.rejectPendingSupplier(id, rejectReason);
        toast.success('Supplier rejected');
        handleCloseReviewModal();
        fetchData();
      } catch {
        toast.error('Failed to reject supplier');
      } finally {
        setActionLoading(null);
      }
    }
    setRejectDialogOpen(false);
    setRejectTarget(null);
    setRejectReason('');
  };

  const needle = searchQuery.trim().toLowerCase();
  const matchesSearch = (...fields: Array<string | undefined | null>) =>
    !needle || fields.some((f) => typeof f === 'string' && f.toLowerCase().includes(needle));

  const filteredInvitations = invitations.filter(inv =>
    matchesSearch(inv.invitee_company_name, inv.invitee_email)
  );
  const filteredDraftSuppliers = draftSuppliers.filter(s =>
    matchesSearch(s.company_name, s.supplier_code)
  );
  const filteredApprovedSuppliers = approvedSuppliers.filter(s =>
    matchesSearch(s.company_name, s.supplier_code)
  );
  const filteredPendingInitial = pendingInitialSuppliers.filter(p =>
    matchesSearch((p as unknown as Record<string, string>)['company_name'])
  );
  const filteredPendingReviews = pendingReviews.filter(fd =>
    matchesSearch(fd.supplier_name, (fd as unknown as Record<string, string>)['dataset_name'])
  );

  // Reset paging whenever the search changes
  useEffect(() => { setDraftPage(1); setApprovedPage(1); }, [searchQuery]);

  const paginate = <T,>(items: T[], page: number): T[] =>
    items.slice((page - 1) * TAB_PAGE_SIZE, page * TAB_PAGE_SIZE);
  const pageCount = (total: number) => Math.max(1, Math.ceil(total / TAB_PAGE_SIZE));

  const pagedDraftSuppliers = paginate(filteredDraftSuppliers, draftPage);
  const pagedApprovedSuppliers = paginate(filteredApprovedSuppliers, approvedPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isSupplier) {
    return (
      <SupplierOnboardingView
        myFilledDatasets={myFilledDatasets}
        myWorkflow={myWorkflow}
        onRefresh={fetchSupplierData}
      />
    );
  }

  return (
    <div className="p-6 space-y-6" data-tour="onboarding-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Supplier Onboarding</h1>
          <p className="text-muted-foreground">Manage supplier invitations, onboarding workflows, and questionnaires</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/suppliers/wizard/new')}><Plus className="h-4 w-4 mr-2" />Add supplier</Button>
          <Button variant="outline" onClick={fetchData}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Pending Invitations</CardTitle><Mail className="h-4 w-4 text-muted-foreground" /></CardHeader>
          <CardContent><div className="text-2xl font-bold">{stats?.invitations?.pending || stats?.invitations?.sent || 0}</div><p className="text-xs text-muted-foreground">{(stats?.invitations as unknown as Record<string, number>)?.['total'] || 0} total invitations</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Active Onboardings</CardTitle><Clock className="h-4 w-4 text-muted-foreground" /></CardHeader>
          <CardContent><div className="text-2xl font-bold">{(stats?.workflows as unknown as Record<string, number>)?.['active'] || stats?.workflows?.in_progress || 0}</div><p className="text-xs text-muted-foreground">Suppliers completing onboarding steps</p></CardContent>
        </Card>
        <Card data-tour="pending-approvals">
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Pending Due Diligence</CardTitle><AlertTriangle className="h-4 w-4 text-orange-500" /></CardHeader>
          <CardContent><div className="text-2xl font-bold">{stats?.workflows?.under_review || ((stats?.workflows as unknown as Record<string, Record<string, number>>)?.['by_status']?.['submitted'] || 0)}</div><p className="text-xs text-muted-foreground">{((stats as unknown as Record<string, Record<string, number>>)?.['questionnaires']?.['awaiting_review']) || 0} questionnaires to review</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium">Approved</CardTitle><CheckCircle2 className="h-4 w-4 text-green-500" /></CardHeader>
          <CardContent><div className="text-2xl font-bold">{stats?.workflows?.approved || ((stats?.workflows as unknown as Record<string, Record<string, number>>)?.['by_status']?.['approved'] || 0)}</div><p className="text-xs text-muted-foreground">Suppliers fully onboarded</p></CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search suppliers..." value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} className="pl-9" />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="drafts" className="flex items-center gap-2">
            <CircleDashed className="h-4 w-4" />Drafts
            {draftSuppliers.length > 0 && (<Badge variant="secondary" className="ml-1">{draftSuppliers.length}</Badge>)}
          </TabsTrigger>
          <TabsTrigger value="approvals" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />Pending Approvals
            {(pendingInitialSuppliers.length + pendingApprovals.length) > 0 && (
              <Badge variant="destructive" className="ml-1">{pendingInitialSuppliers.length + pendingApprovals.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />Invited
            {invitations.filter(i => i.status === 'sent' || i.status === 'opened').length > 0 && (
              <Badge variant="secondary" className="ml-1">{invitations.filter(i => i.status === 'sent' || i.status === 'opened').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reviews" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />Pending Due Diligence
            {/* CR-4794: badge counts distinct suppliers awaiting review, not
                questionnaires. A supplier with 3 questionnaires outstanding
                should count as 1 — the tab is a per-supplier worklist. */}
            {(() => {
              const uniqueSuppliers = new Set(pendingReviews.map(p => p.supplier));
              return uniqueSuppliers.size > 0 ? (
                <Badge variant="destructive" className="ml-1">{uniqueSuppliers.size}</Badge>
              ) : null;
            })()}
          </TabsTrigger>
          <TabsTrigger value="declined" className="flex items-center gap-2">
            <ThumbsDown className="h-4 w-4" />Declined
            {/* CR-4837: match the other tabs' count indicator. Source is the
                same union the Declined panel renders (declined workflows +
                declined / cancelled invitations). */}
            {(() => {
              const declinedCount =
                workflows.filter(w => w.status === 'declined').length +
                invitations.filter(i => i.status === 'declined' || i.status === 'cancelled').length;
              return declinedCount > 0 ? (
                <Badge variant="outline" className="ml-1">{declinedCount}</Badge>
              ) : null;
            })()}
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />Approved
            {approvedSuppliers.length > 0 && (<Badge variant="outline" className="ml-1">{approvedSuppliers.length}</Badge>)}
          </TabsTrigger>
        </TabsList>

        {/* Drafts Tab — wizard in progress, not yet submitted for customer review */}
        <TabsContent value="drafts" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Draft Suppliers</CardTitle>
              <CardDescription>Wizard in progress. Submit the wizard to move the supplier to Pending Approvals.</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredDraftSuppliers.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-3" />
                  <p className="text-sm text-muted-foreground">{searchQuery ? 'No drafts match your search' : 'No draft suppliers'}</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Started By</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead className="w-[120px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedDraftSuppliers.map((sp) => (
                        <TableRow key={sp.id}>
                          <TableCell><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{sp.company_name || `Supplier #${sp.id}`}</span></div></TableCell>
                          <TableCell className="text-muted-foreground">{sp.started_by || <span className="italic">Imported</span>}</TableCell>
                          <TableCell className="text-muted-foreground">{sp.created_at ? formatDistanceToNow(new Date(sp.created_at), { addSuffix: true }) : '—'}</TableCell>
                          <TableCell><Button size="sm" variant="outline" onClick={() => navigate(`/suppliers/wizard/${sp.company_id ?? sp.company}`)}><Eye className="h-4 w-4 mr-1" />Continue</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {pageCount(filteredDraftSuppliers.length) > 1 && (
                    <div className="flex items-center justify-between pt-4 text-sm">
                      <span className="text-muted-foreground">Showing {((draftPage - 1) * TAB_PAGE_SIZE) + 1}–{Math.min(draftPage * TAB_PAGE_SIZE, filteredDraftSuppliers.length)} of {filteredDraftSuppliers.length}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={draftPage === 1} onClick={() => setDraftPage(p => Math.max(1, p - 1))}>Previous</Button>
                        <Button size="sm" variant="outline" disabled={draftPage >= pageCount(filteredDraftSuppliers.length)} onClick={() => setDraftPage(p => Math.min(pageCount(filteredDraftSuppliers.length), p + 1))}>Next</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approvals Tab - kept inline due to review modal complexity */}
        <TabsContent value="approvals" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Suppliers Awaiting Initial Approval</CardTitle>
              <CardDescription>New suppliers submitted via the Add Supplier wizard. Review and approve to send invitation with questionnaire.</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredPendingInitial.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-3" />
                  <p className="text-sm text-muted-foreground">{searchQuery ? 'No matches in pending approvals' : 'No suppliers pending initial approval'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead><TableHead>Risk Level</TableHead><TableHead>Est. Annual Spend</TableHead>
                      <TableHead>Submitted By</TableHead><TableHead>Submitted</TableHead><TableHead className="w-[100px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPendingInitial.map((supplier) => (
                      <TableRow key={supplier.id}>
                        <TableCell><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{supplier.company_name}</span></div></TableCell>
                        <TableCell>{supplier.risk_level && (<Badge className={supplier.risk_level === 'high' ? 'bg-red-100 text-red-800' : supplier.risk_level === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>{supplier.risk_level.toUpperCase()}</Badge>)}</TableCell>
                        <TableCell>{supplier.estimated_annual_spend ? `£${Number(supplier.estimated_annual_spend).toLocaleString()}` : '-'}</TableCell>
                        <TableCell>{supplier.submitted_by || 'Unknown'}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDistanceToNow(new Date(supplier.submitted_at), { addSuffix: true })}</TableCell>
                        <TableCell><Button size="sm" onClick={() => handleOpenReviewModal(supplier.id)}><Eye className="h-4 w-4 mr-1" />Review</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Review Modal */}
          <Dialog open={reviewModalOpen} onOpenChange={(open) => { if (!open) handleCloseReviewModal(); }}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
              ) : supplierDetail ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      <Building2 className="h-5 w-5" />{supplierDetail.company_details.name}
                      {(() => {
                        const answers = supplierDetail.risk_answers || [];
                        const scored = answers.filter(a => a.max_score != null && a.max_score > 0);
                        if (scored.length === 0) return supplierDetail.workflow.initial_risk_level ? (
                          <Badge className={supplierDetail.workflow.initial_risk_level === 'high' ? 'bg-red-100 text-red-800' : supplierDetail.workflow.initial_risk_level === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>{supplierDetail.workflow.initial_risk_level.toUpperCase()}</Badge>
                        ) : null;
                        const total = scored.reduce((s, a) => s + (a.score ?? 0), 0);
                        const max = scored.reduce((s, a) => s + (a.max_score ?? 0), 0);
                        const pct = max > 0 ? Math.round((total / max) * 100) : 0;
                        const level = pct >= 66 ? 'HIGH' : pct >= 33 ? 'MEDIUM' : 'LOW';
                        const color = pct >= 66 ? 'bg-red-100 text-red-800' : pct >= 33 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800';
                        return <Badge className={color}>{level}</Badge>;
                      })()}
                    </DialogTitle>
                    <DialogDescription>Review supplier details and risk assessment before approving or rejecting.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-5 py-2">
                    {/* CR-5391: Show only fields that the onboarding wizard
                        captures, so the popup never advertises data the form
                        never collected (Website / Reg No. / Employee Count
                        were the original offenders). */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Company Details</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {([
                          { label: 'Company Name', value: supplierDetail.company_details.name || '-' },
                          { label: 'Email', value: supplierDetail.company_details.primary_email },
                          { label: 'Phone', value: supplierDetail.company_details.primary_phone },
                          { label: 'Address', value: [supplierDetail.company_details.address_line_1, supplierDetail.company_details.city, supplierDetail.company_details.country].filter(Boolean).join(', ') },
                        ] as Array<{ label: string; value: string | null | undefined }>)
                          .filter(item => item.value && item.value !== '-' ? true : item.label === 'Company Name')
                          .map((item, idx) => (
                            <div key={idx} className="bg-muted/30 p-3 rounded-md border">
                              <p className="text-xs text-muted-foreground">{item.label}</p>
                              <p className="text-sm font-medium">{item.value || '-'}</p>
                            </div>
                          ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {supplierDetail.workflow.estimated_annual_spend && (
                        <div className="bg-muted/30 p-3 rounded-md border"><p className="text-xs text-muted-foreground">Est. Annual Spend</p><p className="text-sm font-medium">{`£${Number(supplierDetail.workflow.estimated_annual_spend).toLocaleString()}`}</p></div>
                      )}
                      {supplierDetail.workflow.submitted_at && (
                        <div className="bg-muted/30 p-3 rounded-md border"><p className="text-xs text-muted-foreground">Submitted At</p><p className="text-sm font-medium">{format(new Date(supplierDetail.workflow.submitted_at), 'MMM d, yyyy HH:mm')}</p></div>
                      )}
                      {supplierDetail.workflow.submitted_by && (
                        <div className="bg-muted/30 p-3 rounded-md border"><p className="text-xs text-muted-foreground">Submitted By</p><p className="text-sm font-medium">{supplierDetail.workflow.submitted_by}</p></div>
                      )}
                    </div>
                    {supplierDetail.risk_answers.length > 0 ? (
                      <div>
                        <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Risk Assessment Answers</h4>
                        <div className="space-y-2">
                          {supplierDetail.risk_answers.map((answer, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-md border">
                              <span className="text-xs font-bold text-muted-foreground mt-0.5 shrink-0">{idx + 1}.</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm">{answer.question}</p>
                                <p className="text-sm font-medium mt-1 text-blue-700">{answer.answer || 'No answer'}</p>
                              </div>
                              {answer.max_score != null && (<Badge variant="outline" className="text-xs shrink-0">{answer.score ?? 0} / {answer.max_score}</Badge>)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3"><p className="text-sm text-amber-800">No risk assessment answers were recorded for this submission.</p></div>
                    )}

                    {/* Spend + Risk Score Summary Badges */}
                    {(() => {
                      const answers = supplierDetail.risk_answers || [];
                      const scoredAnswers = answers.filter(a => a.max_score != null && a.max_score > 0);
                      const totalScore = scoredAnswers.reduce((sum, a) => sum + (a.score ?? 0), 0);
                      const maxPossible = scoredAnswers.reduce((sum, a) => sum + (a.max_score ?? 0), 0);
                      const riskPct = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;
                      const riskLevel = riskPct >= 66 ? 'High' : riskPct >= 33 ? 'Medium' : 'Low';
                      const riskColor = riskPct >= 66 ? 'bg-red-100 text-red-800' : riskPct >= 33 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800';
                      const spend = supplierDetail.workflow.estimated_annual_spend;

                      return (scoredAnswers.length > 0 || spend) ? (
                        <div className="flex flex-wrap items-center gap-2 pt-2">
                          {spend && (
                            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-sm px-3 py-1">
                              Spend: £{Number(spend).toLocaleString()}
                            </Badge>
                          )}
                          {scoredAnswers.length > 0 && (
                            <Badge className={`${riskColor} hover:${riskColor} text-sm px-3 py-1`}>
                              Risk Score: {totalScore}/{maxPossible} ({riskPct}%)
                            </Badge>
                          )}
                          {scoredAnswers.length > 0 && (
                            <Badge className={`${riskColor} hover:${riskColor} text-sm px-3 py-1`}>
                              Risk Level: {riskLevel}
                            </Badge>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  {/* Questionnaire Assignment */}
                  {availableTemplates.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Assign Questionnaires</h4>
                      <p className="text-xs text-muted-foreground mb-2">Select which questionnaires to assign when approving this supplier.</p>
                      <div className="space-y-2">
                        {availableTemplates.map((t) => (
                          <label key={t.key} className="flex items-center gap-3 p-2 rounded-md border hover:bg-muted/30 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedTemplateIds.includes(t.key)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedTemplateIds(prev => [...prev, t.key]);
                                else setSelectedTemplateIds(prev => prev.filter(k => k !== t.key));
                              }}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <div>
                              <p className="text-sm font-medium">{t.name}</p>
                              <p className="text-xs text-muted-foreground">{t.type} — {t.questions_count} questions</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-muted-foreground hidden sm:block">Approving will create an invitation{selectedTemplateIds.length > 0 ? ` and assign ${selectedTemplateIds.length} questionnaire(s)` : ''}.</p>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Button variant="outline" onClick={() => { if (supplierDetail?.id) { setReviewModalOpen(false); navigate(`/suppliers/wizard/${supplierDetail.company_id}?step=1`); } }}>Open supplier wizard</Button>
                      <Button variant="destructive" onClick={() => reviewSupplierId && handleRejectPendingSupplier(reviewSupplierId)} disabled={actionLoading === `supplier-${reviewSupplierId}`}><ThumbsDown className="h-4 w-4 mr-2" />Reject</Button>
                      <Button onClick={() => reviewSupplierId && handleApprovePendingSupplier(reviewSupplierId)} disabled={actionLoading === `supplier-${reviewSupplierId}`}>
                        {actionLoading === `supplier-${reviewSupplierId}` ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ThumbsUp className="h-4 w-4 mr-2" />}Approve & Send Invitation
                      </Button>
                    </div>
                  </DialogFooter>
                </>
              ) : null}
            </DialogContent>
          </Dialog>

          {/* Invitation Approvals */}
          {pendingApprovals.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Invitations Awaiting Approval</CardTitle><CardDescription>Review and approve invitations before they are sent to suppliers</CardDescription></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead>Risk Level</TableHead><TableHead>Est. Spend</TableHead><TableHead>Submitted By</TableHead><TableHead className="w-[150px]">Actions</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingApprovals.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{invitation.invitee_company_name}</span></div></TableCell>
                        <TableCell><div><div className="font-medium">{invitation.invitee_contact_name}</div><div className="text-sm text-muted-foreground">{invitation.invitee_email}</div></div></TableCell>
                        <TableCell><Badge className={statusColors[invitation.initial_risk_level] || statusColors['pending']}>{invitation.initial_risk_level?.toUpperCase()}</Badge></TableCell>
                        <TableCell>{invitation.estimated_annual_spend ? `£${Number(invitation.estimated_annual_spend).toLocaleString()}` : '-'}</TableCell>
                        <TableCell><span className="text-sm">{invitation.submitted_for_approval_by_name || invitation.invited_by_name || '-'}</span></TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleApproveToSend(invitation.id)} disabled={actionLoading === invitation.id}>
                              {actionLoading === invitation.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ThumbsUp className="h-4 w-4 mr-1" />Approve</>}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRejectApproval(invitation.id)} disabled={actionLoading === invitation.id}><ThumbsDown className="h-4 w-4 mr-1" />Reject</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="invitations" className="mt-4">
          <InvitationsTab filteredInvitations={filteredInvitations.filter(i => ['sent', 'opened', 'pending'].includes(i.status))} onSendInvitation={handleSendInvitation} onResendInvitation={handleResendInvitation} />
        </TabsContent>

        <TabsContent value="reviews" className="mt-4">
          <PendingReviewsTab pendingReviews={filteredPendingReviews} onAction={fetchData} />
        </TabsContent>

        <TabsContent value="declined" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Declined Suppliers</CardTitle>
              <CardDescription>Suppliers that were rejected during the onboarding process</CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const declinedWorkflows = workflows.filter(w => w.status === 'declined');
                const declinedInvitations = invitations.filter(i => i.status === 'declined' || i.status === 'cancelled');
                const allDeclined = [
                  ...declinedWorkflows.map(w => ({
                    id: `wf-${w.id}`,
                    name: w.supplier_name || 'Unknown',
                    reason: w.decline_reason || 'No reason provided',
                    date: w.declined_at || w.started_at,
                    type: 'workflow' as const,
                  })),
                  ...declinedInvitations.map(i => ({
                    id: `inv-${i.id}`,
                    name: i.invitee_company_name || 'Unknown',
                    reason: i.rejection_reason || 'No reason provided',
                    date: i.sent_at,
                    type: 'invitation' as const,
                  })),
                ].filter(d => matchesSearch(d.name, d.reason));

                if (allDeclined.length === 0) {
                  return (
                    <div className="text-center py-6">
                      <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-3" />
                      <p className="text-sm text-muted-foreground">No declined suppliers</p>
                    </div>
                  );
                }

                return (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Declined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allDeclined.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{item.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[300px]">
                            <p className="text-sm text-muted-foreground truncate">{item.reason}</p>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {item.type === 'workflow' ? 'Onboarding' : 'Invitation'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.date ? formatDistanceToNow(new Date(item.date), { addSuffix: true }) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approved Tab — fully onboarded suppliers with audit trail */}
        <TabsContent value="approved" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Approved Suppliers</CardTitle>
              <CardDescription>Suppliers who have completed onboarding. Click to open their profile.</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredApprovedSuppliers.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-3" />
                  <p className="text-sm text-muted-foreground">{searchQuery ? 'No approved suppliers match your search' : 'No approved suppliers'}</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Approved By</TableHead>
                        <TableHead>Approved On</TableHead>
                        <TableHead className="w-[120px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedApprovedSuppliers.map((sp) => (
                        <TableRow key={sp.id}>
                          <TableCell><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{sp.company_name || `Supplier #${sp.id}`}</span></div></TableCell>
                          <TableCell className="text-muted-foreground">{sp.approved_by || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{sp.approved_at ? format(new Date(sp.approved_at), 'dd MMM yyyy') : '—'}</TableCell>
                          <TableCell><Button size="sm" variant="outline" onClick={() => navigate(`/suppliers/${sp.company_id ?? sp.company}`)}><Eye className="h-4 w-4 mr-1" />View</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {pageCount(filteredApprovedSuppliers.length) > 1 && (
                    <div className="flex items-center justify-between pt-4 text-sm">
                      <span className="text-muted-foreground">Showing {((approvedPage - 1) * TAB_PAGE_SIZE) + 1}–{Math.min(approvedPage * TAB_PAGE_SIZE, filteredApprovedSuppliers.length)} of {filteredApprovedSuppliers.length}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={approvedPage === 1} onClick={() => setApprovedPage(p => Math.max(1, p - 1))}>Previous</Button>
                        <Button size="sm" variant="outline" disabled={approvedPage >= pageCount(filteredApprovedSuppliers.length)} onClick={() => setApprovedPage(p => Math.min(pageCount(filteredApprovedSuppliers.length), p + 1))}>Next</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reject Reason Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={(open) => { if (!open) { setRejectDialogOpen(false); setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ThumbsDown className="h-5 w-5" />
              Reject Supplier
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this supplier. This will be recorded in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setRejectDialogOpen(false); setRejectTarget(null); setRejectReason(''); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmReject} disabled={!rejectReason.trim()}>
              <ThumbsDown className="h-4 w-4 mr-2" />
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
